import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  SmartToy as SmartToyIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Notifications as NotificationsIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  SignalCellularAlt as SignalIcon,
  AccountBalance as TokenIcon,
  Public as ExchangeIcon,
  VpnLock as ProxyIcon,
  Analytics as AnalyticsIcon,
  ExpandLess,
  ExpandMore,
  Storage as DatabaseIcon,
  BarChart as StatsIcon,
  Speed as PerformanceIcon,
  CheckCircle as ReservedIcon,
  Timeline as UsageIcon,
  Assessment as ProxyAnalyticsIcon,
  ShowChart as TradeAnalyticsIcon,
  Insights as SignalAnalyticsIcon,
  MonetizationOn as TokenAnalyticsIcon,
  SwapHoriz as ExchangeAnalyticsIcon,
  BugReport as LogsIcon,
  Schedule as ScheduledJobsIcon,
  History as JobHistoryIcon,
  Security as SecurityIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
}

interface MenuCategory {
  category: string;
  items: MenuItem[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedMenus, setExpandedMenus] = useState<{ [key: string]: boolean }>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuCategories: MenuCategory[] = [
    {
      category: 'Overview',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      ],
    },
    {
      category: 'User Management',
      items: [
        {
          text: 'Users',
          icon: <PeopleIcon />,
          children: [
            { text: 'User Management', icon: <ManageAccountsIcon />, path: '/users' },
            { text: 'Permissions', icon: <SecurityIcon />, path: '/permissions' },
          ],
        },
      ],
    },
    {
      category: 'Trading',
      items: [
        {
          text: 'Bots',
          icon: <SmartToyIcon />,
          children: [
            { text: 'Bot Management', icon: <DatabaseIcon />, path: '/bots' },
            { text: 'Bot Statistics', icon: <StatsIcon />, path: '/bots/statistics' },
            { text: 'Performance', icon: <PerformanceIcon />, path: '/bots/performance' },
          ],
        },
        {
          text: 'Trades',
          icon: <TrendingUpIcon />,
          children: [
            { text: 'Trade Management', icon: <DatabaseIcon />, path: '/trades' },
            { text: 'Trade Analytics', icon: <TradeAnalyticsIcon />, path: '/trades/analytics' },
          ],
        },
        {
          text: 'Signals',
          icon: <SignalIcon />,
          children: [
            { text: 'Signal Management', icon: <DatabaseIcon />, path: '/signals' },
            { text: 'Signal Analytics', icon: <SignalAnalyticsIcon />, path: '/signals/analytics' },
          ],
        },
      ],
    },
    {
      category: 'Market Data',
      items: [
        {
          text: 'Tokens',
          icon: <TokenIcon />,
          children: [
            { text: 'Token Management', icon: <DatabaseIcon />, path: '/tokens' },
            { text: 'Token Analytics', icon: <TokenAnalyticsIcon />, path: '/tokens/analytics' },
          ],
        },
        {
          text: 'Exchanges',
          icon: <ExchangeIcon />,
          children: [
            { text: 'Exchange Management', icon: <DatabaseIcon />, path: '/exchanges' },
            { text: 'Exchange Analytics', icon: <ExchangeAnalyticsIcon />, path: '/exchanges/analytics' },
          ],
        },
      ],
    },
    {
      category: 'Infrastructure',
      items: [
        {
          text: 'Proxies',
          icon: <ProxyIcon />,
          children: [
            { text: 'Proxy Management', icon: <DatabaseIcon />, path: '/proxies' },
            { text: 'Proxy Usage', icon: <UsageIcon />, path: '/proxies/usage' },
          ],
        },
        {
          text: 'Jobs',
          icon: <WorkIcon />,
          children: [
            { text: 'Scheduled Jobs', icon: <ScheduledJobsIcon />, path: '/jobs' },
            { text: 'Job History', icon: <JobHistoryIcon />, path: '/jobs/history' },
          ],
        },
        {
          text: 'Logs',
          icon: <LogsIcon />,
          path: '/logs',
        },
      ],
    },
    {
      category: 'Analytics & Reports',
      items: [
        { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
        { text: 'Export', icon: <DownloadIcon />, path: '/export' },
      ],
    },
    {
      category: 'System',
      items: [
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ],
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  const handleExpandClick = (menuText: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuText]: !prev[menuText],
    }));
  };

  const isPathActive = (path?: string, children?: MenuItem[]): boolean => {
    if (path && location.pathname === path) return true;
    if (children) {
      return children.some(child => location.pathname === child.path);
    }
    return false;
  };

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus[item.text];
    const isActive = isPathActive(item.path, item.children);

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            selected={isActive && !hasChildren}
            onClick={() => {
              if (hasChildren) {
                handleExpandClick(item.text);
              } else if (item.path) {
                navigate(item.path);
              }
            }}
            sx={{
              minHeight: 48,
              justifyContent: 'initial',
              px: depth === 0 ? 2.5 : 4,
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 2,
                justifyContent: 'center',
                color: isActive ? 'primary.main' : 'text.secondary',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: depth === 0 ? '0.875rem' : '0.8125rem',
                fontWeight: isActive ? 600 : 500,
              }}
            />
            {hasChildren && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
          GUAC Admin
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        {menuCategories.map((category, index) => (
          <React.Fragment key={category.category}>
            <Box sx={{ px: 2.5, py: 1.5, mt: index === 0 ? 0 : 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: 'text.secondary',
                  fontSize: '0.6875rem',
                }}
              >
                {category.category}
              </Typography>
            </Box>
            <List component="nav" disablePadding>
              {category.items.map(item => renderMenuItem(item))}
            </List>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {(() => {
              for (const category of menuCategories) {
                for (const item of category.items) {
                  if (item.path === location.pathname) return item.text;
                  if (item.children) {
                    const child = item.children.find(c => c.path === location.pathname);
                    if (child) return child.text;
                  }
                }
              }
              return 'Admin Dashboard';
            })()}
          </Typography>
          
          {/* Notifications */}
          <IconButton color="inherit" sx={{ mr: 1 }}>
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Menu */}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuClick}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2">{user?.email}</Typography>
              </ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
